// Viewer toolbar (CONTRACTS §6/§6c). Extracted from DocViewer, which owns all
// the state; this component only lays the actions out and reports intent.
//
// The row is organised by intent rather than by implementation, in four groups
// separated by rules:
//
//   identity   back · title · confidential badge
//   context    presence · version
//   actions    comments · present · share · more        (secondary, ghost)
//   primary    trust · edit                             (filled, consequential)
//
// Rules that keep it legible: every top-level control is icon + label at the
// same height; anything that opens a menu carries a caret; only the two
// consequential actions are filled. Trust is amber and appears ONLY while the
// doc is still sandboxed for this reader, so it reads as a condition to resolve
// rather than a permanent control.
//
// Width tiers: ≤1100px the secondary actions drop to icon-only (aria-label and
// tooltip retained); ≤600px they collapse into one overflow menu. Trust and Edit
// keep their labels at every width — they are the two things worth the pixels.

import type { ReactNode } from "react";
import { Icon } from "./Icon";
import { Menu, type MenuItem } from "./Menu";
import { MoreMenu } from "./MoreMenu";
import { PresenceBar } from "./PresenceBar";
import { ConfidentialBadge } from "./ConfidentialBadge";
import { useIsCompactToolbar, useIsPhone } from "../hooks/useMediaQuery";
import type { PresentUser } from "../presence";
import type { DocMeta, DocVersion } from "../types";

// The comments panel's own close button focuses this on the way out, so the
// reader never loses their place. An id rather than a forwarded ref: the toggle
// is four prop-hops away and only ever exists once on the page.
export const COMMENTS_TOGGLE_ID = "comments-toggle";

interface Props {
  meta: DocMeta;
  versions: DocVersion[];
  resolvedVersionId: string;
  multiFile: boolean;

  isOwner: boolean;
  isEditor: boolean;
  isCurrent: boolean;

  /** Trusted globally (owner) OR personally by this account — no sandbox. */
  effectiveTrusted: boolean;
  personallyTrusted: boolean;

  editing: boolean;
  saveLabel: string;
  commitBusy: boolean;
  saving: boolean;
  unresolvedCount: number;
  commitBlocked: boolean;

  showCommentsToggle: boolean;
  commentsOpen: boolean;
  threadCount: number;

  present: PresentUser[];
  meUid?: string;

  shareOpen: boolean;
  sharePanel: ReactNode;

  onBack: () => void;
  onSelectVersion: (versionId: string) => void;
  onToggleComments: () => void;
  onPresent: () => void;
  onToggleShare: () => void;
  onOpenTrust: () => void;
  onRevokeMyTrust: () => void;
  onEnterEdit: () => void;
  onExitEdit: () => void;
  onCommit: () => void;
  onExportPdf: () => void;
  onExportImage: () => void;
  onDownloadHtml: () => void;
  onDownloadZip: () => void;
  onDuplicate: () => void;
  onViewStats: () => void;
}

export function ViewerToolbar({
  meta,
  versions,
  resolvedVersionId,
  multiFile,
  isOwner,
  isEditor,
  isCurrent,
  effectiveTrusted,
  personallyTrusted,
  editing,
  saveLabel,
  commitBusy,
  saving,
  unresolvedCount,
  commitBlocked,
  showCommentsToggle,
  commentsOpen,
  threadCount,
  present,
  meUid,
  shareOpen,
  sharePanel,
  onBack,
  onSelectVersion,
  onToggleComments,
  onPresent,
  onToggleShare,
  onOpenTrust,
  onRevokeMyTrust,
  onEnterEdit,
  onExitEdit,
  onCommit,
  onExportPdf,
  onExportImage,
  onDownloadHtml,
  onDownloadZip,
  onDuplicate,
  onViewStats,
}: Props) {
  const isPhone = useIsPhone();
  const compact = useIsCompactToolbar();
  // Below the compact tier the secondary actions are icons only; their meaning
  // survives in aria-label + the rich tooltip.
  const label = (text: string) => (compact ? null : <span>{text}</span>);

  // Any member may trust a doc for themselves, so this shows for viewers and
  // editors too — not just the owner. It disappears the moment the sandbox is
  // lifted by either route.
  const showTrust = !effectiveTrusted && !editing;

  // Rendered in BOTH the editing and viewing branches below. It is the only way
  // back once the panel's own × has hidden it, and edit mode is exactly where a
  // reader needs it most — the publish button refuses to commit until every
  // comment is resolved. It also keeps its label at the compact tier: unlike its
  // neighbours this is a toggle, and a lone speech bubble reads as a status
  // badge, which is what made "how do I hide this?" a question at all.
  const commentsToggle = showCommentsToggle && !isPhone && (
    <button
      type="button"
      id={COMMENTS_TOGGLE_ID}
      className={`btn btn-ghost${commentsOpen ? " is-active" : ""}`}
      data-tip={
        commentsOpen
          ? "Hide the comments panel and read full width"
          : "Show the comments panel"
      }
      aria-label={commentsOpen ? "Hide comments" : "Show comments"}
      aria-pressed={commentsOpen}
      aria-expanded={commentsOpen}
      aria-controls="soapbox-comments"
      onClick={onToggleComments}
    >
      <Icon name="message" size={18} />
      <span>Comments</span>
      {threadCount > 0 && <span className="toolbar-count">{threadCount}</span>}
    </button>
  );

  // Phone: one menu for everything that isn't Trust or Edit.
  const phoneItems: MenuItem[] = [
    { label: "Comments", icon: "message", onSelect: onToggleComments },
    { label: "Present full screen", icon: "present", onSelect: onPresent },
    { label: "Export PDF…", icon: "pdf", onSelect: onExportPdf },
    { label: "Export image…", icon: "image", onSelect: onExportImage },
    multiFile
      ? { label: "Download bundle (.zip)", icon: "download", onSelect: onDownloadZip }
      : { label: "Download HTML", icon: "download", onSelect: onDownloadHtml },
    { label: "Duplicate", icon: "copy", onSelect: onDuplicate },
    { label: "View stats…", icon: "chart", onSelect: onViewStats },
    ...(personallyTrusted
      ? [
          {
            label: "Remove my trust",
            icon: "shield" as const,
            onSelect: onRevokeMyTrust,
          },
        ]
      : []),
    ...(isOwner
      ? [{ label: "Share…", icon: "share" as const, onSelect: onToggleShare }]
      : []),
  ];

  return (
    <div className="viewer-toolbar">
      <button
        type="button"
        className="btn btn-ghost viewer-back"
        data-tip="Back to the workspace"
        aria-label="Back to the workspace"
        onClick={onBack}
        disabled={editing}
      >
        <Icon name="arrow-left" size={18} />
        <span className="viewer-back-label">Documents</span>
      </button>
      <strong className="viewer-title">{meta.title}</strong>
      <ConfidentialBadge compact />
      <span style={{ marginLeft: "auto" }} />

      <PresenceBar users={present} meUid={meUid} />

      {editing ? (
        <>
          {commentsToggle}
          {commentsToggle && <span className="toolbar-sep" aria-hidden="true" />}
          <span className="edit-status caption" aria-live="polite">
            {saveLabel}
          </span>
          <span className="toolbar-sep" aria-hidden="true" />
          <button
            type="button"
            className="btn btn-ghost"
            data-tip="Stop editing — your draft is saved, but nothing is published yet"
            onClick={onExitEdit}
            disabled={commitBusy}
          >
            Done editing
          </button>
          <button
            type="button"
            className="btn btn-primary"
            data-tip={
              unresolvedCount > 0
                ? "Resolve all comments before you can publish a new version"
                : "Publish this draft as a new immutable version"
            }
            onClick={onCommit}
            disabled={commitBusy || saving || commitBlocked}
          >
            {commitBusy ? "Publishing…" : "Save as new version"}
          </button>
        </>
      ) : (
        <>
          <span className="toolbar-sep" aria-hidden="true" />
          <label htmlFor="version-switcher" className="visually-hidden">
            Version
          </label>
          <select
            id="version-switcher"
            className="input version-select"
            data-tip="Switch between published versions of this document"
            value={resolvedVersionId}
            onChange={(e) => onSelectVersion(e.target.value)}
          >
            {versions.map((v) => (
              <option key={v.versionId} value={v.versionId}>
                {v.label || v.versionId}
                {v.versionId === meta.currentVersion ? " (current)" : ""}
              </option>
            ))}
          </select>
          <span className="toolbar-sep" aria-hidden="true" />

          {!isPhone && (
            <>
              {commentsToggle}
              <button
                type="button"
                className="btn btn-ghost"
                data-tip="Present — full screen, no app chrome (Esc to exit)"
                aria-label="Present full screen"
                onClick={onPresent}
              >
                <Icon name="present" size={18} />
                {label("Present")}
              </button>
              {isOwner && (
                <div className="share-anchor">
                  <button
                    type="button"
                    className="btn btn-ghost"
                    data-tip="Share — grant view or edit access to other @cellery.ai people"
                    aria-label="Share this document"
                    onClick={onToggleShare}
                    aria-haspopup="dialog"
                    aria-expanded={shareOpen}
                  >
                    <Icon name="share" size={18} />
                    {label("Share")}
                  </button>
                  {shareOpen && sharePanel}
                </div>
              )}
              <MoreMenu
                multiFile={multiFile}
                iconOnly={compact}
                personallyTrusted={personallyTrusted}
                onDownloadHtml={onDownloadHtml}
                onDownloadZip={onDownloadZip}
                onExportPdf={onExportPdf}
                onExportImage={onExportImage}
                onDuplicate={onDuplicate}
                onViewStats={onViewStats}
                onRevokeMyTrust={onRevokeMyTrust}
              />
            </>
          )}

          {isPhone && (
            <Menu
              ariaLabel="More actions"
              align="right"
              triggerClassName="btn btn-ghost icon-btn"
              trigger={<Icon name="more" size={18} />}
              items={phoneItems}
            />
          )}

          {(showTrust || (isEditor && isCurrent)) && (
            <span className="toolbar-sep" aria-hidden="true" />
          )}

          {showTrust && (
            <button
              type="button"
              className="btn btn-warn tip-end"
              data-tip="This document's scripts are blocked. Trust it to let them run."
              aria-label="Trust this document"
              onClick={onOpenTrust}
            >
              <Icon name="shield" size={18} />
              <span>Trust</span>
            </button>
          )}

          {isEditor && isCurrent && (
            <button
              type="button"
              className="btn btn-primary tip-end"
              data-tip="Edit this document inline (creates a shared draft other editors can join)"
              onClick={onEnterEdit}
            >
              <Icon name="edit" size={18} />
              <span>Edit</span>
            </button>
          )}
        </>
      )}
    </div>
  );
}
